import { env } from 'src/app/services/PlugpointEnvironment';
import { Injectable } from '@angular/core';
import createAuth0Client from '@auth0/auth0-spa-js';
import Auth0Client from '@auth0/auth0-spa-js/dist/typings/Auth0Client';
import * as config from '../../../auth_config.json';
import * as config_test from '../../../auth_config_test.json'
import * as config_demo from '../../../auth_config_demo.json'
import { from, of, Observable, BehaviorSubject, combineLatest, throwError, Subscription } from 'rxjs';
import { tap, catchError, concatMap, shareReplay, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { UserService } from '../services/user/user.service';
import { User } from '../services/user/user';
import { Dealer } from '../services/dealer/dealerData';
import { DealerService } from '../services/dealer/dealer.service';
import { AUTH_CONFIG } from './auth0.config';
import { HttpClient } from '@angular/common/http';
import { SignUpObj } from './signup';
import { LogService } from '../services/logs/log.service';
import { ActivityLog } from '../services/logs/log';
import { UserStateService } from '../services/user/user-state.service';
import { PlugpointEnvironment } from 'src/app/services/PlugpointEnvironment';


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // AUTH_CONFIG_TEST (TEST) -- AUTH_CONFIG (PROD) - check also connectionToken();
  domain = AUTH_CONFIG.domainAPI;
  // Create an observable of Auth0 instance of client
  refactoredId: string = "nologgedin";
  userEmail: string;
  
  // SECURITY: Private role/access with integrity protection
  private _userRole: string = "";
  private _access: string = "";
  private _integrityHash: string = "";
  private _roleLoadedFromBackend: boolean = false;
  
  userVendor: string = "none";
  userName: string = "none";
  userTagId: string = "N/A";
  userId: number = 0;
  subClients$: Subscription;
  clientsList: Dealer[];
  private isLoadingUserData: boolean = false;

  // SECURITY: Getter with integrity check
  get userRole(): string {
    if (!this._roleLoadedFromBackend) {
      return this._userRole; // Return empty during loading
    }
    if (!this.verifyIntegrity()) {
      console.error('SECURITY: Role integrity check failed - possible tampering!');
      this.handleTamperingDetected();
      return '';
    }
    return this._userRole;
  }

  // SECURITY: Getter with integrity check
  get access(): string {
    if (!this._roleLoadedFromBackend) {
      return this._access;
    }
    if (!this.verifyIntegrity()) {
      console.error('SECURITY: Access integrity check failed - possible tampering!');
      this.handleTamperingDetected();
      return 'none';
    }
    return this._access;
  }

  // SECURITY: Check if role was legitimately loaded from backend
  get isRoleVerified(): boolean {
    return this._roleLoadedFromBackend && this.verifyIntegrity();
  }

  private generateIntegrityHash(): string {
    const data = `${this._userRole}:${this._access}:${this.refactoredId}:${this.userEmail}`;
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return btoa(hash.toString());
  }

  private verifyIntegrity(): boolean {
    if (!this._integrityHash) return true; // No hash yet = still loading
    return this._integrityHash === this.generateIntegrityHash();
  }

  private handleTamperingDetected(): void {
    console.error('SECURITY ALERT: Role/access tampering detected - resetting role data');
    this._userRole = '';
    this._access = 'none';
    this._roleLoadedFromBackend = false;
    this._integrityHash = '';
    // Note: Not calling logout() to allow error investigation during registration
    // If needed, implement delayed logout or manual intervention
  }

  private setRoleAndAccess(role: string, access: string): void {
    this._userRole = role;
    this._access = access;
    this._integrityHash = this.generateIntegrityHash();
    this._roleLoadedFromBackend = true;
    
    // Additional protection: Log this event for audit trail
    console.log('Role and access set:', { role, access, timestamp: new Date().toISOString() });
  }

  private clearRoleData(): void {
    this._userRole = '';
    this._access = 'none';
    this._integrityHash = '';
    this._roleLoadedFromBackend = false;
  }

  // SECURITY: Test method to verify integrity protection is working
  // To test in browser console: authService.testIntegrityProtection()
  public testIntegrityProtection(): void {
    console.log('=== INTEGRITY PROTECTION TEST ===');
    console.log('Current role:', this.userRole);
    console.log('Current access:', this.access);
    console.log('Is role verified:', this.isRoleVerified);
    
    // Attempt to tamper with private properties
    console.log('\nAttempting to tamper with role directly...');
    (this as any)._userRole = 'admin'; // Force tampering
    
    console.log('After tampering, trying to access role...');
    const tamperedRole = this.userRole;
    console.log('Got role:', tamperedRole, '(should be empty string due to failed integrity check)');
    
    console.log('Is role still verified:', this.isRoleVerified);
    console.log('=== TEST COMPLETE ===');
  }

  auth0Client$ = (from(
      env == "prod" ?
          createAuth0Client({
          domain: config.domain,
          client_id: config.clientId,
          redirect_uri: `${window.location.origin}`,
          cacheLocation: 'localstorage',
          useRefreshTokens: true,
          audience: AUTH_CONFIG.audience,
          scope: 'openid profile email'
      }):
          createAuth0Client({
          domain: config_demo.domain,
          client_id: config_demo.clientId,
          redirect_uri: `${window.location.origin}`,
          cacheLocation: 'localstorage',
          useRefreshTokens: true,
          audience: AUTH_CONFIG.audience,
          scope: 'openid profile email'
      })
    // ***IMPORTANT******* CHECK LOGOUT TO CHANGE!!!!!!!!!! FOR PROD/TEST
    // ***IMPORTANT******* CHECK LOGOUT TO CHANGE!!!!!!!!!! FOR PROD/TEST
    // ***IMPORTANT******* CHECK LOGOUT TO CHANGE!!!!!!!!!! FOR PROD/TEST
  ) as Observable<Auth0Client>).pipe(
    shareReplay(1), // Every subscription receives the same shared value
    catchError(err => {
      console.error('Auth0 Client initialization error:', err);
      return throwError(err);
    })
  );
  // Define observables for SDK methods that return promises by default
  // For each Auth0 SDK method, first ensure the client instance is ready
  // concatMap: Using the client instance, call SDK method; SDK returns a promise
  // from: Convert that resulting promise into an observable
  isAuthenticated$ = this.auth0Client$.pipe(
    concatMap((client: Auth0Client) => from(client.isAuthenticated())),
    tap(res => this.loggedIn = res)
  );
  handleRedirectCallback$ = this.auth0Client$.pipe(
    concatMap((client: Auth0Client) => from(client.handleRedirectCallback())),
    // tap(res => console.log("res: ", res))
  );
  // Create subject and public observable of user profile data
  private userProfileSubject$ = new BehaviorSubject<any>(null);
  userProfile$ = this.userProfileSubject$.asObservable();

  // Create a local property for login status
  loggedIn: boolean = null;

  constructor(private router: Router, private dealerService: DealerService, private userService: UserService, public http: HttpClient,
    private logService: LogService, private userStateService: UserStateService
  ) {
    // this.userProfile$.subscribe(
    //   (user) => {
    //     console.log("userOBSERVABLE: ", user);
    //   }
    // )
    // On initial load, check authentication state with authorization server
    // Set up local auth streams if user is already authenticated
    this.localAuthSetup();
    // Handle redirect from Auth0 login
    this.handleAuthCallback();
  }

  // When calling, options can be passed if desired
  // https://auth0.github.io/auth0-spa-js/classes/auth0client.html#getuser
  getUser$(options?): Observable<any> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getUser(options))),
      tap(user => {
        this.userProfileSubject$.next(user)
        // console.log("user: ", user);
      })
    );
  }

  private localAuthSetup() {
    // This should only be called on app initialization
    // Set up local authentication streams
    // Skip if this is a callback redirect (handleAuthCallback will handle it)
    const params = window.location.search;
    if (params.includes('code=') && params.includes('state=')) {
      return; // Let handleAuthCallback handle the user data loading
    }

    const checkAuth$ = this.isAuthenticated$.pipe(
      concatMap((loggedIn: boolean) => {
        if (loggedIn) {
          // If authenticated, get user and set in app
          // NOTE: you could pass options here if needed
          this.checkLocalUserDb();
          return this.getUser$();
        }
        // If not authenticated, return stream that emits 'false'
        return of(loggedIn);
      }),
      catchError(err => {
        console.error('localAuthSetup error:', err);
        return of(false);
      })
    );
    checkAuth$.subscribe();
  }

  /**
   * Ensure user data is loaded before guarded routes run. If nothing is loaded yet,
   * trigger checkLocalUserDb() and wait (bounded) for role/access to populate.
   */
  async ensureUserDataLoaded(timeoutMs: number = 5000): Promise<void> {
    // Fast-path if already have valid data (role must exist and not be empty)
    if (this.userRole && this.userRole !== '') {
      console.log('ensureUserDataLoaded: Data already loaded', { role: this.userRole, access: this.access });
      return;
    }

    console.log('ensureUserDataLoaded: Waiting for user data...', { isLoading: this.isLoadingUserData });

    // Trigger load if not already in progress
    if (!this.isLoadingUserData) {
      this.checkLocalUserDb();
    }

    // Wait until data arrives or timeout
    return new Promise<void>((resolve) => {
      const start = Date.now();
      const intervalId = setInterval(() => {
        // Check for valid role (not empty string)
        if (this.userRole && this.userRole !== '') {
          console.log('ensureUserDataLoaded: Data loaded!', { role: this.userRole, access: this.access });
          clearInterval(intervalId);
          resolve();
          return;
        }
        if (Date.now() - start >= timeoutMs) {
          console.warn('ensureUserDataLoaded: Timeout waiting for user data');
          clearInterval(intervalId);
          resolve();
        }
      }, 100);
    });
  }

  login(redirectPath: string = '/') {
    // A desired redirect path can be passed to login method
    // (e.g., from a route guard)
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) =>{

      // Call method to log in
      client.loginWithRedirect({
        redirect_uri: `${window.location.origin}`,
        appState: { target: redirectPath },
        audience: AUTH_CONFIG.audience,
        scope: 'openid profile email'
      });
    });

  }

  private handleAuthCallback() {
    // Call when app reloads after user logs in with Auth0
    const params = window.location.search;
    if (params.includes('code=') && params.includes('state=')) {
      let targetRoute: string; // Path to redirect to after login processsed
      const authComplete$ = this.handleRedirectCallback$.pipe(
        // Have client, now call method to handle auth callback redirect
        tap(cbRes => {
          // Get and set target redirect route from callback results
          targetRoute = cbRes.appState && cbRes.appState.target ? cbRes.appState.target : '/';
        }),
        concatMap(() => {
          // Redirect callback complete; get user and login status
          if (!this.userRole) this.checkLocalUserDb();
          return combineLatest([
            this.getUser$(),
            this.isAuthenticated$
          ]);
        })
      );
      // Subscribe to authentication completion observable
      // Response will be an array of user and login status
      authComplete$.subscribe(([user, loggedIn]) => {
        // Redirect to target route after callback processing
        this.router.navigate([targetRoute]);
      });
    }
  }

  logout() {
    // Ensure Auth0 client instance exists
    this.clearRoleData();
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log out
      client.logout({
        client_id: env == "prod" ? config.clientId : config_demo.clientId,
        returnTo: window.location.origin
      });
    });
    localStorage.clear();
  }

  checkLocalUserDb() {
    // Prevent duplicate API calls - but allow retry if previous call failed (role is still empty)
    if (this.isLoadingUserData) {
      console.log('checkLocalUserDb: Already loading, skipping');
      return;
    }
    if (this._userRole && this._userRole !== '') {
      console.log('checkLocalUserDb: Data already loaded, skipping');
      return;
    }
    console.log('checkLocalUserDb: Starting data load...');
    this.isLoadingUserData = true;
    this._access = '';
    var bar = new Promise<void>((resolve, reject) => {
      this.getUser$().subscribe(user => {
        this.refactoredId = <string>user.sub;
        var indexId = this.refactoredId.indexOf("|");
        this.refactoredId = this.refactoredId.substring(indexId + 1);
        this.userEmail = <string>user.email;
        this.userName = this.refactoredId;
        this.userStateService.setUserName(this.userName);
        resolve();
      });
    });

    bar.then(() => {
      if (this.refactoredId) {
        this.getUserToken$().subscribe(token => {
            this.userService.getUserDataFromGateway(this.refactoredId).subscribe(
                userData => {
                    if (!userData.userEmail || userData.userEmail === null) {
                        console.log('User exists but has incomplete data (null role/email), initializing...');
                        this.userService.addUserToWs(this.userEmail, this.refactoredId, token).subscribe(
                            resp => {
                                this.setRoleAndAccess('guest', 'none');
                                console.log('User initialized as guest successfully:', resp);
                                this.isLoadingUserData = false;
                            }, 
                            error => {
                                console.error('Failed to initialize user:', error);
                                console.error('Error details:', {
                                    status: error.status,
                                    statusText: error.statusText,
                                    message: error.message,
                                    error: error.error
                                });
                                // Set guest role anyway to prevent lockout
                                this.setRoleAndAccess('guest', 'none');
                                this.isLoadingUserData = false;
                            }
                        );
                        return;
                    }
                    
                    // User has valid data, use it
                    const role = userData.role;
                    const access = userData.access || 'none';
                    
                    // Update email from backend if available
                    if (userData.userEmail) {
                        this.userEmail = userData.userEmail;
                    }
                    
                    // SECURITY: Set role AFTER all data is finalized (hash includes userEmail)
                    this.setRoleAndAccess(role, access);
                    
                    console.log("USER role: ", this.userRole);
                    this.userVendor = userData.stationVendor;
                    console.log("USER VENDOR: ", this.userVendor);
                    this.userTagId = userData.tagId || 'N/A';
                    this.userId = userData.userId || 0;

                    if(this.userRole == 'dealer' || this.userRole == 'god') this.getClients(token);
                    this.isLoadingUserData = false;
                },
                error => {
                    console.log('User not found in gateway, attempting to register...');
                    this.userService.addUserToWs(this.userEmail, this.refactoredId, token).subscribe(
                        resp => {
                            // SECURITY: Use setter for guest users too
                            this.setRoleAndAccess('guest', 'none');
                            console.log('User added as guest successfully:', resp);
                            this.isLoadingUserData = false;

                        }, error => {
                            console.error('Failed to add user to WS:', error);
                            console.error('Error details:', {
                                status: error.status,
                                statusText: error.statusText,
                                message: error.message,
                                error: error.error
                            });
                            // Set guest role anyway to prevent lockout
                            this.setRoleAndAccess('guest', 'none');
                            this.isLoadingUserData = false;
                        }
                    );
                }
            );
        });
      }
    });
  }
  getClients(token: string) {
    if (this.userVendor != null && this.userVendor != "") {
      this.subClients$ = this.dealerService.getDealer(this.userVendor, token).subscribe(client => {
        if (client.data) {
          var response = (client);
          if (response !== null) {
            this.clientsList = <Dealer[]>(response);
            // Store in memory only - no localStorage
          } else {
            this.clientsList = [];
          }
        } else {
        }

      })
    }
  }

  /**
   * Get user's JWT token for authenticated requests to gateway
   * @public - Available for components to get JWT for gateway API calls
   */
  getUserToken$(): Observable<string> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getTokenSilently({
        audience: AUTH_CONFIG.audience
      }).catch(err => {
        console.error('Error getting token silently:', err);
        // If token retrieval fails, trigger re-login
        if (err.error === 'login_required') {
          this.login();
        }
        throw err;
      })))
    );
  }
  getAuth0UserInfo(email: string): Observable<any> {
    return this.getUserToken$().pipe(
      switchMap(token => {
        const url = `${PlugpointEnvironment.url}/gateway/api/auth0/user-by-email/${encodeURIComponent(email)}`;
        return this.http.get<any>(url, {
          headers: { 'Authorization': 'Bearer ' + token }
        });
      })
    );
  }

  blockUser(id: string): Observable<any> {
    return this.getUserToken$().pipe(
      switchMap(token => {
        const url = `${PlugpointEnvironment.url}/gateway/api/auth0/block-user`;
        return this.http.post<any>(url, { user_id: id }, {
          headers: { 'Authorization': 'Bearer ' + token }
        });
      })
    );
  }

  unblockUser(id: string): Observable<any> {
    return this.getUserToken$().pipe(
      switchMap(token => {
        const url = `${PlugpointEnvironment.url}/gateway/api/auth0/unblock-user`;
        return this.http.post<any>(url, { user_id: id }, {
          headers: { 'Authorization': 'Bearer ' + token }
        });
      })
    );
  }

  getUser(id: string): Observable<any> {
    return this.getUserToken$().pipe(
      switchMap(token => {
        const url = `${PlugpointEnvironment.url}/gateway/api/auth0/user/${id}`;
        return this.http.get<any>(url, {
          headers: { 'Authorization': 'Bearer ' + token }
        });
      })
    );
  }

  unblockAuth0Ip(ip: string): Observable<any> {
    return this.getUserToken$().pipe(
      switchMap(token => {
        const url = `${PlugpointEnvironment.url}/gateway/api/auth0/unblock-ip`;
        return this.http.post<any>(url, { ip_address: ip }, {
          headers: { 'Authorization': 'Bearer ' + token }
        });
      })
    );
  }
}
